import {
  Button,
  ButtonSize,
  ButtonVariant,
  CharacterCard as CharacterCardV2,
  CharacterCardActions,
  CharacterCardDisplayMode,
  CharacterCardVariant,
  IconVariants,
  ListEmpty,
  Spinner,
  SpinnerSize,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import {
  BannerType,
  CHARACTER_MODERATION_STATUS,
  CharacterType,
  ICharacter,
  setMyChatbotSortBy,
  useAppDispatch,
  useAppSelector,
  useCms,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import { CmsBanner, CmsBannerService } from "@/components";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import ModerationWarningModal from "@/components/common/ModerationWarningModal";
import { FilterKeys } from "@/components/type";
import useApi from "@/components/useApi";
import { EXTERNAL_PERMISSIONS, FEATURE_FLAGS, ROUTES, SPICYCHAT_DISCORD } from "@/constants";
import { useCenterElementDetection, useFeatureFlag, useGroupChatDrafts, useMyChatbotsData } from "@/hooks";
import { TabNames } from "@/pages/ChatbotMy/types";
import { OutletContextType } from "@/pages/Creations/CreationLayout";
import { AvatarPreset } from "@/types";
import {
  convertToSnakeCase,
  getAvatarUrl,
  getCharacterCardStatusBadgeLabel,
  getCharacterCardVariantLabel,
  getDraftCardsThumbnailImage,
  getNumberOfCols,
  getProfilePathForCharacter,
  getResolvedGroupChatDraftIds,
} from "@/utils";
import { getEmptyStateValue } from "@/utils/getEmptyStateValue";

import { CHARACTER_CARD_VARIANT, colGap, colWidth } from "../ChatbotMy/constants";

export default function GroupChats() {
  const { t } = useTranslation();
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();
  const { getInlineBannersRowMapping } = useCms();
  const { blurNSFW } = useUserSettings();
  const { callApi: callDeleteApi, isLoading: isDeleteLoading, jsonApiService } = useApi();
  const [selectedCharacterInfo, setSelectedCharacterInfo] = useState<ICharacter | null>(null);
  const characterCardContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { permissions } = useUserInfo();
  const { groupChatDrafts, deleteDraft } = useGroupChatDrafts();
  const sortBy = useAppSelector((state: { settings: { myChatbotSortBy: string } }) => state.settings.myChatbotSortBy);

  const {
    chatbots,
    filteredAndSortedChatbots,
    isLoading,
    isFiltering,
    isLoadingMoreGroupchats,
    shouldShowLoadMoreButton,
    localSearchQuery,
    localFilterBy,
    handleLocalSearch,
    handleLocalFilter,
    handleLocalSort,
    refetch,
    loadMoreChatbots,
  } = useMyChatbotsData({
    initialSortType: convertToSnakeCase(sortBy),
    type: CharacterType.META,
  });

  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [moderationModalVisible, setModerationModalVisible] = useState(false);
  const hasPermissionToDelete = permissions?.includes(EXTERNAL_PERMISSIONS.delete_character);

  const characterId = selectedCharacterInfo?.id;

  const containerWidth = Math.max(characterCardContainerRef.current?.clientWidth || 400, 400);
  const containerPadding = isMobile ? 10 : 16;
  const numberOfColumns = useMemo(
    () =>
      getNumberOfCols({
        containerWidth,
        containerPadding,
        colWidth,
        colGap,
      }),
    [containerWidth, isMobile],
  );
  const { centeredElementIds, setElementRef } = useCenterElementDetection();

  useEffect(() => {
    if (!chatbots || !groupChatDrafts.length) {
      return;
    }

    // Retire drafts through `useGroupChatDrafts` so the write stays on the group chat draft key.
    getResolvedGroupChatDraftIds(groupChatDrafts, chatbots).forEach((draftId: string) => deleteDraft(draftId));
  }, [chatbots, groupChatDrafts, deleteDraft]);

  const [search, filter, sort] = useOutletContext<OutletContextType>();

  useEffect(() => {
    handleLocalSearch(search);
    handleLocalFilter(filter.key, filter.value);
    dispatch(setMyChatbotSortBy(sort));
    const sortType = convertToSnakeCase(sort);
    handleLocalSort(sortType);
  }, [search, filter.key, filter.value, sort, handleLocalFilter, handleLocalSearch, handleLocalSort, convertToSnakeCase]);

  const getCharacterInfoById = useCallback(
    (characterIdToLookup: string) => {
      let characterData;
      // only get character info when the character is different form current one
      if (characterId !== characterIdToLookup) {
        characterData = filteredAndSortedChatbots?.find((character: ICharacter) => character?.id === characterIdToLookup);

        if (characterData) {
          setSelectedCharacterInfo(characterData);
        }
      }
      return characterData || selectedCharacterInfo;
    },
    [characterId, selectedCharacterInfo, filteredAndSortedChatbots],
  );

  const handleClose = () => {
    setShowDeleteConfirmationDialog(false);
    setShowBanDialog(false);
  };

  const handleDelete = (characterId: string) => {
    getCharacterInfoById(characterId);
    if (!hasPermissionToDelete) return setShowBanDialog(true);
    setShowDeleteConfirmationDialog(true);
  };

  const deleteCharacter = async (id: string) => {
    await callDeleteApi(jsonApiService.deleteResource, `characters/${id}`).then(async () => {
      await refetch();
    });
  };

  const rowMapping = CmsBannerService.contentfulScreen && getInlineBannersRowMapping(CmsBannerService.contentfulScreen);
  const totalNumberOfRows = filteredAndSortedChatbots ? filteredAndSortedChatbots?.length / numberOfColumns : 0;
  const shouldShowInlineBanner = (index: number) => {
    if (!index) {
      return false;
    }
    const itemRowNumber = index / numberOfColumns;
    return totalNumberOfRows > itemRowNumber;
  };

  const handleCardClick = (characterId: string) => {
    const characterData = getCharacterInfoById(characterId);
    const moderationStatus = characterData?.moderationReport?.moderation_status;
    if (!moderationStatus || moderationStatus === CHARACTER_MODERATION_STATUS.CLOSED) {
      navigate(`/chat/${characterId}`);
    } else {
      setModerationModalVisible(true);
    }
  };

  const characterCardHandler = (action: CharacterCardActions, characterId: string) => {
    const characterData = getCharacterInfoById(characterId);
    if (!characterData) return;
    switch (action) {
      case CharacterCardActions.CHAT:
        handleCardClick(characterId);
        break;
      case CharacterCardActions.INFO:
        navigate(getProfilePathForCharacter(characterData));
        break;
    }
  };

  return (
    <>
      <div ref={characterCardContainerRef} className="w-full h-full flex flex-col">
        {isLoading || !filteredAndSortedChatbots ? (
          <div className="flex justify-center items-center w-full h-full">
            <Spinner size={SpinnerSize.LARGE} className="mx-auto" />
          </div>
        ) : (
          <>
            {isFiltering ? (
              <div className="flex justify-center items-center h-full">
                <Spinner className="mx-auto" />
              </div>
            ) : (
              <>
                {!filteredAndSortedChatbots?.length && !groupChatDrafts?.length && (
                  <div className=" flex justify-center items-center h-full">
                    <ListEmpty
                      isLoading={false}
                      {...getEmptyStateValue({
                        searchedQuery: localSearchQuery,
                        filterBy: localFilterBy,
                        navigate,
                        activeTab: TabNames.GROUPS,
                        t,
                      })}
                    />
                  </div>
                )}
                {(!!groupChatDrafts?.length || !!filteredAndSortedChatbots?.length) && (
                  <div
                    className="mt-lg grid !gap-y-3 pb-4 max-mob:pb-2"
                    style={{
                      gridTemplateColumns: `repeat(auto-fill, minmax(${colWidth}px, 1fr))`,
                      gap: colGap,
                    }}
                  >
                    {!!groupChatDrafts?.length && !localSearchQuery && localFilterBy === FilterKeys.ALL && (
                      <CharacterCardV2
                        variant={CharacterCardVariant.DRAFTS}
                        characterInfo={{
                          characterId: "",
                          characterName: "",
                          characterCreatorUserName: "",
                          characterTitle: "",
                          characterMessageNumber: 0,
                          characterAvatarUrl: "",
                          characterDraftCount: groupChatDrafts?.length,
                          characterDraftImages: getDraftCardsThumbnailImage(groupChatDrafts),
                          draftsPagePath: ROUTES.groupchatsDrafts,
                          draftsCountLabel: t("myCreation:page.draftsCount", { count: groupChatDrafts?.length }),
                        }}
                      />
                    )}
                    {(filteredAndSortedChatbots || [])?.map((groupChatInfo: ICharacter, idx: number) => {
                      const moderationStatus = groupChatInfo?.moderationReport?.moderation_status;
                      const activeModerationStatus = moderationStatus !== "closed" ? moderationStatus : undefined;
                      const manualReviewRequired = groupChatInfo.aicq_manual_review_required ? "manual_review_required" : "";
                      const variant =
                        CHARACTER_CARD_VARIANT[
                          (manualReviewRequired || activeModerationStatus || groupChatInfo?.visibility) as keyof typeof CHARACTER_CARD_VARIANT
                        ];
                      const variantLabel = getCharacterCardVariantLabel(variant, t);
                      const statusBadgeLabel = getCharacterCardStatusBadgeLabel(variant, t);
                      const isCharacterAvatarBlur = blurNSFW && groupChatInfo?.avatar_is_nsfw;

                      return (
                        <Fragment key={groupChatInfo?.id}>
                          {rowMapping?.[idx / numberOfColumns]?.length && shouldShowInlineBanner(idx) ? (
                            <div className=" w-full col-span-full">
                              <CmsBanner
                                placement={BannerType.Inline}
                                isVisibleOnPartner={false}
                                availableBanners={rowMapping?.[idx / numberOfColumns]}
                              />
                            </div>
                          ) : null}
                          <div ref={(el: HTMLDivElement | null) => setElementRef(groupChatInfo?.id ?? "", el)}>
                            <CharacterCardV2
                              characterInfo={{
                                characterId: groupChatInfo?.id ?? "",
                                characterName: groupChatInfo?.name,
                                characterCreatorUserName: groupChatInfo?.creator_username,
                                characterTitle: groupChatInfo?.title,
                                characterMessageNumber: groupChatInfo?.num_messages || "0",
                                characterAvatarUrl:
                                  getAvatarUrl(groupChatInfo?.avatar_url as string, {
                                    preset: AvatarPreset.AVATAR_256X256,
                                  }) || "",
                                characterDefinitionVisible: groupChatInfo?.definition_visible,
                                characterInfoLabel: groupChatInfo?.definition_visible
                                  ? t("chatbotCard:group.tooltip.definitionVisible")
                                  : t("chatbotCard:group.tooltip.definitionHidden"),
                                characterTokensCount: groupChatInfo?.token_count,
                                hasVoice: hasVoiceAttachmentFeatureFlag && !!groupChatInfo?.voice_id,
                                lorebookAttached: !!groupChatInfo?.has_lorebooks,
                                lorebookLabel: groupChatInfo?.has_lorebooks ? t("chatbotCard:tooltip.lorebookAttached"): undefined,
                                creatorPagePath: `/creator/${groupChatInfo?.creator_username}`,
                                chatPagePath:
                                  moderationStatus && moderationStatus !== CHARACTER_MODERATION_STATUS.CLOSED
                                    ? undefined
                                    : `/chat/${groupChatInfo?.id}`,
                                characterAvatarBlur: isCharacterAvatarBlur,
                                characterAicq: groupChatInfo.aicq_manual_review_required === true,
                                variantLabel,
                                statusBadgeLabel,
                                groupCountLabel: t("chatbotCard:group.tooltip.participantCount"),
                              }}
                              characterCardActionsHandler={characterCardHandler}
                              characterCardActionsState={{
                                [CharacterCardActions.FAVORITE]: {
                                  isHidden: true,
                                },
                              }}
                              variant={variant}
                              displayMode={CharacterCardDisplayMode.STATUS}
                              membersCount={groupChatInfo.sub_characters_count}
                              dropdownMenuActions={[
                                {
                                  label: t("myCreation:card.actions.edit"),
                                  icon: IconVariants.EDIT,
                                  to: `${ROUTES.groupchatEdit}/${groupChatInfo?.id}`,
                                  onPress: () => {},
                                },
                                {
                                  label: t("myCreation:card.actions.remove"),
                                  icon: IconVariants.TRASH,
                                  onPress: () => handleDelete(groupChatInfo?.id ?? ""),
                                  isDisabled: false,
                                  isHidden: false,
                                  className: "text-red-9",
                                },
                              ]}
                              atCenter={centeredElementIds.includes(groupChatInfo?.id ?? "")}
                            />
                          </div>
                        </Fragment>
                      );
                    })}
                  </div>
                )}
                {shouldShowLoadMoreButton && (
                  <Row justify="center" className="my-lg">
                    <Button
                      loading={isLoadingMoreGroupchats}
                      onClick={() => void loadMoreChatbots()}
                      size={ButtonSize.LARGE}
                      variant={ButtonVariant.OUTLINED}
                      data-testid="GroupChatsMy-LoadMoreButton"
                    >
                      <Trans i18nKey="common:loadMore" />
                    </Button>
                  </Row>
                )}
              </>
            )}
          </>
        )}
      </div>
      {!!showDeleteConfirmationDialog && (
        <ConfirmationModal
          heading={t("myCreation:modal.removeChatbot.title")}
          handleClose={handleClose}
          isLoading={isDeleteLoading}
          onUserConfirmation={async () => {
            if (characterId) {
              await deleteCharacter(characterId);
            }
            return;
          }}
          confirmationMessage={t("myCreation:modal.removeChatbot.description", { name: selectedCharacterInfo?.name })}
          isVisible={showDeleteConfirmationDialog}
          actionButtonColor="error"
          actionButtonText={t("common:remove")}
          discardButtonText={t("common:cancel")}
        />
      )}
      {!!showBanDialog && (
        <ConfirmationModal
          handleClose={handleClose}
          heading={t("myCreation:modal.restricted.title")}
          confirmationMessage={t("myCreation:modal.restricted.description")}
          link={SPICYCHAT_DISCORD}
          linkText={t("myCreation:modal.restricted.contactUs")}
          isVisible={showBanDialog}
          showButtons={false}
          isLoading={false}
        />
      )}
      {!!moderationModalVisible && (
        <ModerationWarningModal
          moderationModalVisible={moderationModalVisible}
          setModerationModalVisible={setModerationModalVisible}
          character={selectedCharacterInfo as ICharacter}
        />
      )}
    </>
  );
}
